<script setup>
import { ref, useSlots, watch, nextTick } from "vue";

const props = defineProps({
    /**
     * Whether the modal is visible or not.
     */
    visible: {
        type: Boolean,
        default: false,
    },
    /**
     * Additional classes to apply to the modal.
     */
    class: {
        type: String,
        default: "",
    },
    /**
     * The transition to use for the modal.
     */
    transition: {
        type: String,
        default: undefined,
    },
    /**
     * The aria label for the close button.
     */
    ariaCloseLabel: {
        type: String,
        default: "Close modal",
    },
    /**
     * Whether to disable the backdrop filter.
     */
    disableBackdropFilter: {
        type: Boolean,
        default: false,
    },
    /**
     * Function to call when the enter key is pressed inside the modal
     */
    onEnter: {
        type: Function,
        default: () => {},
    },
});

const emit = defineEmits(["update:visible"]);
const slots = useSlots();
const showModal = ref(props.visible);

const closeModal = () => {
    showModal.value = false;
    emit("update:visible", false);
};

watch(
    () => props.visible,
    async newVal => {
        showModal.value = newVal;

        if (newVal) {
            // Wait for DOM update after showModal changes
            await nextTick();
            // Find and focus the close button
            const closeButton = document.querySelector(".qmodii-modal-close");
            if (closeButton) {
                closeButton.focus();
            }
        }
    }
);
</script>

<template lang="pug">
teleport(to="body")
    transition(:name="props.transition")
        .qmodii-modal-container.qmodii-ui-tool(
            v-show="showModal",
            :class="props.class",
            @keydown.enter="props.onEnter"
        )
            .qmodii-modal-backdrop(
                @click="closeModal",
                :class="{ 'no-backdrop-filter': props.disableBackdropFilter }"
            )
            .qmodii-modal
                .qmodii-modal-content
                    .qmodii-modal-head
                        .qmodii-modal-header
                            slot(name="header")
                            button.qmodii-modal-close(
                                autofocus,
                                @click="closeModal",
                                :aria-label="props.ariaCloseLabel",
                                tabindex="0"
                            )
                                i.qm_icon-close-med
                        .qmodii-modal-subheader
                            slot(name="subheader")
                    .qmodii-modal-body.qmodii-custom-scrollbar
                        slot
                    .qmodii-modal-footer(v-if="slots.footer")
                        slot(name="footer")
</template>

<style>
:root {
    --qmodii-modal-spacing: var(--qmodii-spacing-lg);
    --qmodii-modal-border-radius: var(--qmodii-border-radius-md);
}
</style>

<style lang="stylus" scoped>
.qmodii-anchor-bottom-right
    .qmodii-modal
        right 50px
        top auto
        left auto
        bottom 50px
        transform none

.qmodii-anchor-inline
    .qmodii-modal
        position absolute
        right 0
        top 0
        left auto
        transform none
        height 100%
        border-radius 0

        .qmodii-modal-content
            height 100%
            min-height 0

        .qmodii-modal-body
            max-height none
            flex-grow 1

.qmodii-modal-backdrop
    position fixed
    width 100%
    height 100%
    background rgba(0, 0, 0, 0.5)
    backdrop-filter blur(2px)
    z-index 999
    top 0
    left 0

    &.no-backdrop-filter
        backdrop-filter none
        background none

.qmodii-modal
    position fixed
    top 50%
    left 50%
    transform translate(-50%, -50%)
    width 500px
    max-width 95vw
    background white
    border var(--qmodii-primary-border)
    border-radius var(--qmodii-border-radius-md)
    box-shadow 0 4px 6px rgba(0, 0, 0, 0.1)
    z-index 1000
    padding var(--qmodii-modal-spacing) 0

    .qmodii-modal-content
        display flex
        flex-direction column
        gap var(--qmodii-modal-spacing)

    .qmodii-modal-head
        display flex
        flex-direction column
        align-items start
        gap var(--qmodii-spacing-sm)

    .qmodii-modal-header
        display flex
        flex-direction column
        align-items start
        gap 8px
        padding 0 var(--qmodii-modal-spacing)

        > *
            margin 0

        .qmodii-modal-close
            position absolute
            top var(--qmodii-modal-spacing)
            right var(--qmodii-modal-spacing)
            margin-left auto
            cursor pointer
            font-size 1em
            font-weight bold
            border none
            background none
            padding 0

            &:hover
                color var(--qmodii-primary)

    .qmodii-modal-subheader
        display flex
        flex-direction column
        gap 8px
        padding 0 var(--qmodii-modal-spacing)

        > *
            margin 0
            font-weight normal
            color var(--qmodii-secondary-text)

    .qmodii-modal-body
        display flex
        flex-direction column
        gap var(--qmodii-modal-spacing)
        flex-grow 1
        max-height 50vh
        overflow auto
        padding var(--qmodii-spacing-xs) var(--qmodii-modal-spacing)

    .qmodii-modal-footer
        display flex
        justify-content end
        padding 0 var(--qmodii-modal-spacing)

    .qmodii-heading
        line-height 1

.fade-enter-active
.fade-leave-active
    transition opacity 0.2s ease

.fade-enter-from
.fade-leave-to
    opacity 0

.slide-fade-enter-active
.slide-fade-leave-active
    transition all 0.3s ease

.slide-fade-enter-from
.slide-fade-leave-to
    opacity 0
    transform translateY(-20px)

.slide-enter-active
.slide-leave-active
    transition transform 0.3s ease

.slide-enter-from
.slide-leave-to
    transform translateY(-20px)
</style>
